import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Star, CheckCircle2, XCircle, Copy } from 'lucide-react';
import { Skill, SkillRiskLevel, SKILL_RISK_COLOR } from './skills/types';
import { SKILLS } from './skills/mockData';
import { SkillIcon } from './skills/iconMap';
import { InstallSheet } from './skills/InstallSheet';
type Filter = 'all' | 'installed' | 'needs-review' | 'high' | 'low';
const FILTER_CHIPS: {
  id: Filter;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'installed',
  label: 'Installed'
}, {
  id: 'needs-review',
  label: 'Needs review'
}, {
  id: 'high',
  label: 'High risk'
}, {
  id: 'low',
  label: 'Low risk'
}];
export function SkillsView() {
  const [skills, setSkills] = useState<Skill[]>(SKILLS);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [installingId, setInstallingId] = useState<string | null>(null);
  const handleInstalled = (id: string) => {
    setSkills(prev => prev.map(s => s.id === id ? {
      ...s,
      status: 'installed',
      lastUsed: 'just now'
    } : s));
    setInstallingId(null);
  };
  const filteredSkills = useMemo(() => {
    let list = [...skills];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q) || s.domain.toLowerCase().includes(q));
    }
    if (filter === 'installed') list = list.filter(s => s.status === 'installed');
    if (filter === 'needs-review') list = list.filter(s => s.status === 'pending-review');
    if (filter === 'high') list = list.filter(s => s.risk === 'high');
    if (filter === 'low') list = list.filter(s => s.risk === 'low');
    return list;
  }, [skills, search, filter]);
  if (viewingId) {
    const skill = skills.find(s => s.id === viewingId);
    if (skill) {
      return <SkillDetail skill={skill} onBack={() => setViewingId(null)} onInstall={() => {
        setViewingId(null);
        setInstallingId(skill.id);
      }} data-id="element-1764" />;
    }
  }
  const installed = filteredSkills.filter(s => s.status === 'installed');
  const pendingReview = filteredSkills.filter(s => s.status === 'pending-review');
  const discovered = filteredSkills.filter(s => s.status === 'discovered');
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1765">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-1766">
        <header className="mb-6" data-id="element-1767">
          <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight" data-id="element-1768">Skills</h1>
          <p className="text-[13px] text-[#8a8680] mt-1" data-id="element-1769">Capabilities from the clawhub registry.</p>
        </header>

        <div className="relative mb-5" data-id="element-1770">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8a8680]/40" data-id="element-1771" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search skills" className="w-full bg-transparent text-[#e8e4df] pl-6 pr-4 py-2 text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 focus:border-[#8a8680]/30 transition-colors" data-id="element-1772" />
        </div>

        <div className="flex items-center gap-2 mb-8 flex-wrap" data-id="element-1773">
          {FILTER_CHIPS.map(chip => {
          const active = filter === chip.id;
          return <button key={chip.id} onClick={() => setFilter(chip.id)} className="px-3 py-1 rounded-full text-[11px] transition-colors" style={{
            background: active ? '#4ac0d422' : 'rgba(255,255,255,0.03)',
            color: active ? '#4ac0d4' : '#8a8680',
            border: active ? '1px solid #4ac0d455' : '1px solid transparent'
          }} data-id="element-1774">
                {chip.label}
              </button>;
        })}
        </div>

        {installed.length > 0 && <Section label="Installed" count={installed.length} data-id="element-1775">
            {installed.map(skill => <SkillRow key={skill.id} skill={skill} onClick={() => setViewingId(skill.id)} data-id="element-1776" />)}
          </Section>}

        {pendingReview.length > 0 && <Section label="Needs review" count={pendingReview.length} data-id="element-1777">
            {pendingReview.map(skill => <SkillRow key={skill.id} skill={skill} onClick={() => setViewingId(skill.id)} data-id="element-1778" />)}
          </Section>}

        {discovered.length > 0 && <Section label="Browse registry" count={discovered.length} data-id="element-1779">
            {discovered.map(skill => <SkillRow key={skill.id} skill={skill} onClick={() => setViewingId(skill.id)} data-id="element-1780" />)}
          </Section>}

        {filteredSkills.length === 0 && <div className="text-center py-20" data-id="element-1781">
            <div className="text-[13px] text-[#8a8680]/50" data-id="element-1782">No skills match these filters.</div>
          </div>}
      </div>

      <InstallSheet skill={installingId ? skills.find(s => s.id === installingId) ?? null : null} onClose={() => setInstallingId(null)} onInstalled={handleInstalled} data-id="element-1783" />
    </div>;
}
function Section({
  label,
  count,
  children
}: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  return <section className="mb-10" data-id="element-1784">
      <div className="flex items-baseline gap-2 mb-4" data-id="element-1785">
        <h2 className="text-[12px] text-[#8a8680]/60 uppercase tracking-wide" data-id="element-1786">{label}</h2>
        <span className="text-[11px] text-[#8a8680]/40" data-id="element-1787">{count}</span>
      </div>
      <div className="flex flex-col gap-3" data-id="element-1788">{children}</div>
    </section>;
}
function SkillRow({
  skill,
  onClick
}: {
  skill: Skill;
  onClick: () => void;
}) {
  const isPending = skill.status === 'pending-review';
  const isInstalled = skill.status === 'installed';
  return <button onClick={onClick} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" style={{
    borderLeft: isPending ? '3px solid #e8a84c' : '3px solid transparent'
  }} data-id="element-1789">
      <div className="flex items-center gap-4" data-id="element-1790">
        <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-1791">
          <SkillIcon icon={skill.icon} className={isInstalled ? 'text-[#e8e4df]' : 'text-[#8a8680]/70'} data-id="element-1792" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-1793">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-1794">
            <span className={`text-[15px] ${isInstalled ? 'text-[#e8e4df]' : 'text-[#e8e4df]/80'}`} data-id="element-1795">
              {skill.name}
            </span>
            <span className="text-[10px] text-[#8a8680]/50" data-id="element-1796">v{skill.version}</span>
            <RiskBadge risk={skill.risk} data-id="element-1797" />
            <span className="flex items-center gap-0.5 text-[10px] text-[#8a8680]/50" data-id="element-1798">
              <Star size={9} data-id="element-1799" />
              {skill.stars >= 1000 ? `${(skill.stars / 1000).toFixed(1)}k` : skill.stars}
            </span>
          </div>
          <p className="text-[12px] text-[#8a8680]/70 mt-0.5 truncate" data-id="element-1800">{skill.description}</p>
        </div>
        {isInstalled && <div className="w-1.5 h-1.5 rounded-full bg-[#4ac0d4] shrink-0" data-id="element-1801" />}
        {isPending && <span className="text-[11px] text-[#e8a84c] shrink-0 whitespace-nowrap" data-id="element-1802">Needs review</span>}
        {skill.status === 'discovered' && <span className="text-[12px] text-[#8a8680]/40 group-hover:text-[#8a8680] shrink-0" data-id="element-1803">
            Install
          </span>}
      </div>
    </button>;
}
function RiskBadge({
  risk
}: {
  risk: SkillRiskLevel;
}) {
  const color = SKILL_RISK_COLOR[risk];
  return <span className="text-[9px] uppercase tracking-wide rounded-full px-1.5 py-0.5" style={{
    background: `${color}15`,
    color,
    border: `1px solid ${color}33`
  }} data-id="element-1804">
      {risk}
    </span>;
}
function SkillDetail({
  skill,
  onBack,
  onInstall
}: {
  skill: Skill;
  onBack: () => void;
  onInstall: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1805">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-1806">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-1807">
          <ArrowLeft size={14} data-id="element-1808" />
          <span className="text-[13px]" data-id="element-1809">Skills</span>
        </button>

        <header className="mb-6 flex items-center gap-4" data-id="element-1810">
          <div className="w-12 h-12 rounded-[14px] bg-[#252530] flex items-center justify-center" data-id="element-1811">
            <SkillIcon icon={skill.icon} size={22} className="text-[#e8e4df]" data-id="element-1812" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-1813">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-1814">
              <h1 className="text-[20px] font-medium text-[#e8e4df]" data-id="element-1815">{skill.name}</h1>
              <span className="text-[12px] text-[#8a8680]" data-id="element-1816">v{skill.version}</span>
              <RiskBadge risk={skill.risk} data-id="element-1817" />
              {skill.status === 'installed' && <div className="w-1.5 h-1.5 rounded-full bg-[#4ac0d4]" data-id="element-1818" />}
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[12px] text-[#8a8680]" data-id="element-1819">
              <span data-id="element-1820">{skill.author}</span>
              <span className="text-[#8a8680]/30" data-id="element-1821">·</span>
              <span className="flex items-center gap-1" data-id="element-1822">
                <Star size={10} data-id="element-1823" />
                {skill.stars}
              </span>
            </div>
          </div>
        </header>

        <p className="text-[13px] text-[#8a8680] leading-[1.6] mb-6" data-id="element-1824">{skill.description}</p>

        {skill.tools.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1825">
            <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1826">Tools</h3>
            <div className="space-y-3" data-id="element-1827">
              {skill.tools.map(tool => <div key={tool.name} className="pb-3 border-b border-white/[0.03] last:border-0 last:pb-0" data-id="element-1828">
                  <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-1829">
                    <span className="text-[13px] text-[#e8e4df] font-mono" data-id="element-1830">{tool.name}</span>
                    <span className="text-[9px] uppercase tracking-wide text-[#e8a84c] border border-[#e8a84c]/40 rounded-full px-1.5 py-0.5" data-id="element-1831">
                      Approval
                    </span>
                  </div>
                  <div className="text-[12px] text-[#8a8680] mb-1.5" data-id="element-1832">{tool.summary}</div>
                  <div className="text-[10px] text-[#8a8680]/70 font-mono" data-id="element-1833">
                    {tool.inputSchemaPreview}
                  </div>
                </div>)}
            </div>
          </div>}

        <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1834">
          <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1835">Provenance</h3>
          <div className="flex items-center justify-between py-1.5" data-id="element-1836">
            <span className="text-[12px] text-[#8a8680]" data-id="element-1837">Registry</span>
            <span className="text-[13px] text-[#e8e4df]" data-id="element-1838">clawhub / {skill.slug}</span>
          </div>
          <div className="flex items-center justify-between py-1.5" data-id="element-1839">
            <span className="text-[12px] text-[#8a8680]" data-id="element-1840">Bundle sha256</span>
            <div className="flex items-center gap-2" data-id="element-1841">
              <span className="text-[12px] text-[#8a8680]/70 font-mono" data-id="element-1842">
                {skill.sha256.slice(0, 12)}…
              </span>
              <button className="text-[#8a8680]/40 hover:text-[#8a8680]" data-id="element-1843">
                <Copy size={11} data-id="element-1844" />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5" data-id="element-1845">
            <span className="text-[12px] text-[#8a8680]" data-id="element-1846">Execution</span>
            <span className="text-[13px] text-[#e8e4df]" data-id="element-1847">Manual (human-confirmed)</span>
          </div>
        </div>

        {skill.versions && skill.versions.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1848">
            <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1849">Versions</h3>
            {skill.versions.map(v => <div key={v.version} className="flex items-center justify-between py-1.5" data-id="element-1850">
                <span className="text-[13px] text-[#e8e4df]" data-id="element-1851">v{v.version}</span>
                <span className="text-[11px] text-[#8a8680]" data-id="element-1852">approved {v.approvedAt}</span>
              </div>)}
          </div>}

        {skill.audit && skill.audit.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1853">
            <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1854">Recent runs</h3>
            <div className="space-y-2" data-id="element-1855">
              {skill.audit.map(entry => <div key={entry.id} className="flex items-center gap-3 py-1" data-id="element-1856">
                  {entry.status === 'success' ? <CheckCircle2 size={12} className="text-[#4ac0d4]/70" data-id="element-1857" /> : <XCircle size={12} className="text-[#e86c4c]/70" data-id="element-1858" />}
                  <div className="flex-1 min-w-0" data-id="element-1859">
                    <div className="text-[12px] text-[#e8e4df] font-mono truncate" data-id="element-1860">{entry.tool}</div>
                    <div className="text-[10px] text-[#8a8680]/60 truncate" data-id="element-1861">
                      {entry.invokedBy} · approved by {entry.approvedBy} · {entry.durationMs}ms
                    </div>
                  </div>
                  <div className="text-[10px] text-[#8a8680]/60" data-id="element-1862">{entry.timestamp}</div>
                </div>)}
            </div>
          </div>}

        <div className="flex items-center gap-4 mt-6" data-id="element-1863">
          {skill.status === 'discovered' && <button onClick={onInstall} className="text-[13px] text-[#4ac0d4] hover:text-[#5bd0dc] transition-colors" data-id="element-1864">
              Install skill
            </button>}
          {skill.status === 'pending-review' && <button onClick={onInstall} className="text-[13px] text-[#e8a84c] hover:text-[#f0b864] transition-colors" data-id="element-1865">
              Review and approve
            </button>}
          {skill.status === 'installed' && <button className="text-[13px] text-[#8a8680]/60 hover:text-[#8a8680] transition-colors" data-id="element-1866">
              Uninstall
            </button>}
        </div>
      </div>
    </motion.div>;
}